/**
 * InfiniteScrollSentinel - loads the next page when the end of a list scrolls into view.
 *
 * Renders nothing once there is no next page, so a list that fits on one screen has no
 * footer at all. While a page is in flight it says so, because an auto-loading list with
 * no feedback looks stuck.
 *
 * The button is not decoration: `IntersectionObserver` is missing in jsdom and in older
 * browsers, and without a manual way out the list would dead-end at page one. It shows
 * only when the observer could not be attached.
 *
 * The caller decides when the sentinel exists. Mount it whenever `hasNextPage` is true,
 * including when zero rows have arrived - a keyset page can come back empty with a
 * non-null cursor, and hiding the sentinel behind an empty state would stop the paging.
 */
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/Button';

interface InfiniteScrollSentinelProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  /** Distance ahead of the viewport at which the next page starts loading. */
  rootMargin?: string;
  label?: string;
}

export function InfiniteScrollSentinel({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  rootMargin = '200px',
  label = 'Mehr laden',
}: InfiniteScrollSentinelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [observed, setObserved] = useState(false);
  // Read the callback and the two flags through a ref. The observer is created once per
  // mount; a callback captured inside it would keep calling the first render's
  // fetchNextPage, and a stale `isFetchingNextPage` would let one scroll fire the same
  // page several times.
  const latest = useRef({ onLoadMore, hasNextPage, isFetchingNextPage });
  latest.current = { onLoadMore, hasNextPage, isFetchingNextPage };

  // `hasNextPage` is a dependency because the sentinel is not in the DOM until it turns
  // true: on the first render of a list it is still false, and an effect that ran only
  // on mount would never find a node to observe.
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const { hasNextPage: more, isFetchingNextPage: busy } = latest.current;
        if (more && !busy && entries.some((entry) => entry.isIntersecting)) {
          latest.current.onLoadMore();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    setObserved(true);
    return () => observer.disconnect();
  }, [hasNextPage, rootMargin]);

  if (!hasNextPage) return null;

  return (
    <div ref={ref} className="flex justify-center py-3">
      {isFetchingNextPage ? (
        <span className="text-sm text-muted-foreground">Lädt …</span>
      ) : observed ? null : (
        <Button variant="outline" size="sm" onClick={onLoadMore}>
          {label}
        </Button>
      )}
    </div>
  );
}
